/**
 * Sprint Redesign v1 · Baustein 8 + Issues 14/15/16 (Do 03.09.2026, 23:30).
 *
 * Tab-basierte Einzel-Detail-Ansicht mit 5 separaten Kacheln pro Fokustyp:
 *   1. Steckbrief-Kachel (Kurz-Beschreibung + Icon + Tagline)
 *   2. Erkennungsmerkmale-Kachel
 *   3. Tonalität-Kachel
 *   4. Typische erste Impulse-Kachel
 *   5. Mimik-Muster-Kachel
 *   6. Brücken-Frage als hervorgehobener Callout-Block
 *   + „Vollständigen Steckbrief öffnen"-Button (führt zu /dashboard/fokustyp/${slug})
 *
 * Mobile: horizontales Swipe zwischen den 4 Fokustypen. Desktop: Tap.
 * Default-Selection: Primärfokustyp des Users, Fallback = „hund".
 */
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle } from "lucide-react";
import { FOKUSTYP_PROFILES } from "@/lib/fokustypProfiles";
import { STECKBRIEF_ERKENNUNG, SLUG_TO_KEY } from "@/lib/steckbriefErkennung";
import { FokustypIcon } from "@/components/redesign/FokustypIcon";
import SteckbriefKachel from "@/components/redesign/SteckbriefKachel";

const ORDER = ["katze", "eule", "biene", "hund"];

function Card({ children, testId, accent = false }) {
  return (
    <div
      data-testid={testId}
      className={[
        "rounded-2xl border bg-white dark:bg-slate-900 p-5",
        accent
          ? "border-[var(--fts-primary-bright)] shadow-md"
          : "border-slate-200 dark:border-slate-800",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function CardHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] mb-3">
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {title}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1.5">
      {items.map((m, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="mt-1.5 h-1 w-1 rounded-full bg-[var(--fts-primary-bright)] flex-shrink-0" />
          <span className="leading-snug">{m}</span>
        </li>
      ))}
    </ul>
  );
}

function DetailView({ slug, isPrimary }) {
  const profile = FOKUSTYP_PROFILES[SLUG_TO_KEY[slug]] || {};
  const erkennung = STECKBRIEF_ERKENNUNG[slug] || { ready: false };
  const ready = erkennung.ready;

  return (
    <div
      className="space-y-4"
      data-testid={`steckbrief-${slug}`}
      data-primary={isPrimary ? "true" : "false"}
      data-ready={ready ? "true" : "false"}
      data-active="true"
    >
      {/* Kachel 1: Steckbrief-Header */}
      <Card testId={`steckbrief-${slug}-header`} accent={isPrimary}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              Fokustyp {isPrimary && <span className="ml-1 text-[var(--fts-primary-bright)]">· dein Typ</span>}
            </p>
            <h3 className="mt-0.5 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white leading-tight">
              {profile.name || slug}
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {profile.tagline}
            </p>
            {profile.einzeiler && (
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-snug">
                {profile.einzeiler}
              </p>
            )}
          </div>
          <span className="flex-shrink-0" aria-hidden="true">
            <FokustypIcon slug={slug} size={72} />
          </span>
        </div>
      </Card>

      {ready && (
        <>
          {/* Kachel 2: Erkennungsmerkmale */}
          {profile.erkennungsmerkmale?.length > 0 && (
            <SteckbriefKachel
              category="erkennungsmerkmale"
              slug={slug}
              items={profile.erkennungsmerkmale}
              testId={`steckbrief-${slug}-erkennung`}
            />
          )}

          {/* Kachel 3: Tonalität — reiner Prosa-Block, weiterhin Card-Layout */}
          <Card testId={`steckbrief-${slug}-tonalitaet`}>
            <CardHeader icon={HelpCircle} title="Tonalität der Stimme" />
            <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">
              {erkennung.tonalitaet}
            </p>
          </Card>

          {/* Kachel 4: Erste Impulse */}
          <SteckbriefKachel
            category="erste_impulse"
            slug={slug}
            items={erkennung.erste_impulse}
            testId={`steckbrief-${slug}-impulse`}
          />

          {/* Kachel 5: Mimik-Muster */}
          <SteckbriefKachel
            category="mimik"
            slug={slug}
            items={erkennung.mimik}
            testId={`steckbrief-${slug}-mimik`}
          />

          {/* Callout: Brücken-Frage */}
          <div
            className="rounded-2xl bg-[var(--fts-primary-bright)]/10 dark:bg-[var(--fts-primary-bright)]/20 border-l-4 border-[var(--fts-primary-bright)] p-5"
            data-testid={`steckbrief-${slug}-bruecken-frage`}
          >
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] mb-1">
                  Wen erkennst du wieder?
                </p>
                <p className="text-base font-semibold text-slate-900 dark:text-white leading-snug">
                  {erkennung.bruecken_frage}
                </p>
              </div>
            </div>
          </div>

          {/* CTA: Vollständiger Steckbrief (Issue 15 — korrekte URL) */}
          <div className="pt-2">
            <Link
              to={`/dashboard/fokustyp/${slug}`}
              data-testid={`steckbrief-${slug}-vollstaendig`}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-4 py-2.5"
            >
              Vollständigen Steckbrief öffnen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

export default function FokustypSteckbriefe({ primaryFokustyp = null }) {
  const defaultSlug = ORDER.includes(primaryFokustyp) ? primaryFokustyp : "katze";
  const [activeSlug, setActiveSlug] = useState(defaultSlug);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    if (ORDER.includes(primaryFokustyp)) {
      setActiveSlug(primaryFokustyp);
    }
  }, [primaryFokustyp]);

  // Issue 16 — Mobile Swipe zwischen den 4 Typen.
  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - (touchStartY.current || 0);
    touchStartX.current = null;
    touchStartY.current = null;
    // Nur wenn horizontal deutlich > vertikal (kein Scroll-Konflikt)
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const idx = ORDER.indexOf(activeSlug);
    if (idx < 0) return;
    if (dx < 0 && idx < ORDER.length - 1) {
      setActiveSlug(ORDER[idx + 1]);
    } else if (dx > 0 && idx > 0) {
      setActiveSlug(ORDER[idx - 1]);
    }
  };

  return (
    <section
      className="space-y-5"
      data-testid="fokustyp-steckbriefe-pro"
      data-active-slug={activeSlug}
      aria-label="Fokustyp-Steckbrief-Detail"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Tab-Bar */}
      <div
        role="tablist"
        aria-label="Fokustyp auswählen"
        data-testid="steckbrief-tabbar"
        className="grid grid-cols-4 gap-1.5 sm:gap-2 p-1.5 rounded-2xl bg-slate-100 dark:bg-slate-800/60"
      >
        {ORDER.map((slug) => {
          const profile = FOKUSTYP_PROFILES[SLUG_TO_KEY[slug]] || {};
          const isActive = activeSlug === slug;
          const isPrimary = primaryFokustyp === slug;
          return (
            <button
              key={slug}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveSlug(slug)}
              data-testid={`steckbrief-tab-${slug}`}
              data-active={isActive ? "true" : "false"}
              className={[
                "flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 rounded-xl px-2 py-2 sm:py-2.5 transition-all text-xs sm:text-sm font-bold",
                isActive
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100",
              ].join(" ")}
            >
              <FokustypIcon slug={slug} size={28} />
              <span className="flex flex-col items-center sm:items-start leading-tight">
                <span>{profile.name || slug}</span>
                {isPrimary && (
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                    dein Typ
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Swipe-Hinweis nur auf Mobile */}
      <p
        className="sm:hidden text-[10px] text-center text-slate-400 dark:text-slate-500"
        data-testid="steckbrief-swipe-hint"
      >
        ← Wische zwischen den Fokustypen →
      </p>

      {/* Detail: 5 Kacheln + Callout */}
      <DetailView slug={activeSlug} isPrimary={primaryFokustyp === activeSlug} />
    </section>
  );
}
